import {
    PayrollDashboardState,
    PayrollContributionsGraph,
    PayrollDetail,
    EmployeeContributionDetail
} from "@/employer/types";
import * as PayrollService from "@/shared/api/payrollService";
import { FetchState } from "@/shared/types";
import * as storeHelper from "@/shared/storeHelper";
import moment from "moment";
import * as _ from "lodash";
import { formatCurrency } from "@/shared/currency";
import { defineStore } from "pinia";
import { useUserStore } from "@/shared/store/user";

export const initialState: PayrollDashboardState = {
    upcomingPayrollFetchState: FetchState.Uninitialized,
    upcomingPayrolls: [],
    payrollContributionsGraph: {
        canNavigateForward: false,
        canNavigateBackward: false,
        entries: [],
        fetchState: FetchState.Uninitialized,
        page: 1
    },
    employeeContributions: {
        fetchState: FetchState.Uninitialized,
        year: 0,
        planStartDate: null,
        employeeContributionRecords: {}
    }
};

export const usePayrollDashboardStore = defineStore("payrollDashboard", {
    state: (): PayrollDashboardState => (_.cloneDeep(initialState)),
    getters: {
        userStore () {
            return useUserStore();
        },
        canPayrollContributionsGraphNavigateForward () {
            return this.isPayrollContributionsGraphLoading ===
                false &&
                this.payrollContributionsGraph.canNavigateForward;
        },
        canPayrollContributionsGraphNavigateBackward () {
            return this.isPayrollContributionsGraphLoading ===
                false &&
                this.payrollContributionsGraph.canNavigateBackward;
        },
        showUpcomingPayroll: state => state.upcomingPayrollFetchState === FetchState.Loaded,
        isPayrollContributionsGraphLoading: state => state.payrollContributionsGraph.fetchState !== FetchState.Loaded,
        isLoading () {
            return this.isPayrollContributionsGraphLoading;
        },
        isPayrollGraphDataEmpty: state => state.payrollContributionsGraph.entries.length === 0,
        payrollContributionsGraphData () {
            if (this.isPayrollContributionsGraphLoading) {
                return [];
            }

            const entries = this.payrollContributionsGraph.entries || [];
            entries.sort((a, b) => {
                const aTime = moment(a.date);
                const bTime = moment(b.date);

                if (aTime.isBefore(bTime)) {
                    return -1;
                }
                if (bTime.isBefore(aTime)) {
                    return 1;
                }
                return 0;
            });

            const data: any[] = [ [ "Date", "Employer", { role: "style" }, {
                type: "string", role: "tooltip", p: { html: true }
            },
                "Employee", { role: "style" },
                { type: "string", role: "tooltip", p: { html: true } } ] ];
            const employerColor = "#8adac9";
            const employeeColor = "#00af89";
            const projectedBarColor = "#DBEEE8";

            for (const entry of entries) {
                let opacity = 1;
                const makeStroke = (isProjected, color) => {
                    if (isProjected) {
                        return `stroke-color: ${color}; stroke-width: 3; stroke-opacity: 1;`;
                    }

                    return "";
                };

                if (entry.isProjected) {
                    opacity = 0.2;
                }

                const makeTooltipSegment = (title, color, amount, borderColor) => {
                    return `<div>
                  <div style="display: inline-block; vertical-align: top;">
                    <div style="display:inline-block; background-color: ${color};
                    ${borderColor ? "border: 2px solid " + borderColor + ";" : ""}
                    width: 10px; height: 10px; border-radius: 10px; margin: 5px 10px 0px 5px;"> </div>
                  </div>
                  <div style="display: inline-block; padding-right: 5px;">
                    ${title}
                    <br>
                    <strong>${formatCurrency(amount)}</strong>
                  </div>
                </div>`;
                };

                const tooltipHtml = `<div style="padding:5px; min-width: 150px;">
                            <div style="text-align: center;">${moment(entry.date).format("MMM D, YYYY")}</div>
                            ${makeTooltipSegment("Employer",
                    entry.isProjected ? projectedBarColor : employerColor,
                    entry.employerAmount,
                    entry.isProjected ? employerColor : null
                )}
                            ${makeTooltipSegment("Employee",
                    entry.isProjected ? projectedBarColor : employeeColor,
                    entry.employeeAmount,
                    entry.isProjected ? employeeColor : null
                )}
                          </div>`;

                data.push([
                    moment(entry.date).format("MMM D"),
                    entry.employerAmount,
                    `color: ${employerColor}; opacity: ${opacity}; ${makeStroke(entry.isProjected, employerColor)}`,
                    tooltipHtml,
                    entry.employeeAmount,
                    `color: #00af89; opacity: ${opacity}; ${makeStroke(entry.isProjected, employeeColor)}`,
                    tooltipHtml ]);
            }

            return data;
        },
        showContributionGraph: state => state.employeeContributions.fetchState === FetchState.Loaded,
        employeeContributionsByPayDate () {
            if (!this.showContributionGraph) {
                return [];
            }
            const entries = this.employeeContributions.year ?
                this.employeeContributions.employeeContributionRecords[this.employeeContributions.year].employeeContributions
                : [];
            let totalAmount = 0;
            const data: any[] = [ [ "PayDate", "EmployeeAmount" ] ];
            entries.forEach((item, index) => {
                totalAmount = totalAmount + item.amount;
                const monthVal = (index === 0 || index === (entries.length - 1)) ?
                    moment(item.payrollDate).format("MMM").toUpperCase() : "";
                data.push([ monthVal, totalAmount ]);
            });
            if (entries.length === 0) {
                data.push("JAN", 0);
                data.push("DEC", 0);
            }
            return data;
        },
        employerContributionsTotal: state => {
            const latestEntry = _.last(state.payrollContributionsGraph.entries);

            return latestEntry ? latestEntry.employerAmount : 0;
        },
        employeeContributionsTotal: state => {
            const latestEntry = _.last(state.payrollContributionsGraph.entries);

            return latestEntry ? latestEntry.employeeAmount : 0;
        },
        summedContributionsTotal: state => {
            const latestEntry = _.last(state.payrollContributionsGraph.entries);

            return latestEntry ? (latestEntry.employeeAmount + latestEntry.employerAmount) : 0;
        },
        employerContributionsTotalChange: state => {
            const [ previousEntry, currentEntry ] = _.takeRight(state.payrollContributionsGraph.entries, 2);

            if (previousEntry && currentEntry) {
                return currentEntry.employerAmount - previousEntry.employerAmount;
            }

            return 0;
        },
        employeeContributionsTotalChange: state => {
            const [ previousEntry, currentEntry ] = _.takeRight(state.payrollContributionsGraph.entries, 2);

            if (previousEntry && currentEntry) {
                return currentEntry.employeeAmount - previousEntry.employeeAmount;
            }

            return 0;
        },
        summedContributionsTotalChange () {
            return this.employerContributionsTotalChange + this.employeeContributionsTotalChange;
        },
        employeeYearToDateTotal: state => {
            return state.employeeContributions.employeeContributionRecords[state.employeeContributions.year] ?
                state.employeeContributions.employeeContributionRecords[state.employeeContributions.year].processedAmount
                : 0;
        },
        employeeTotalContributionForYear: state => {
            return state.employeeContributions.employeeContributionRecords[state.employeeContributions.year] ?
                state.employeeContributions.employeeContributionRecords[state.employeeContributions.year].totalAmount
                : 0;
        },
        ficaSavingsCurrentYear () {
            const ficaSavingsPercentageValue = 0.0765;
            return this.employeeYearToDateTotal * ficaSavingsPercentageValue;
        },
        employeeContributionsYears () {
            if (!this.showContributionGraph) {
                return [];
            }
            let planYear = moment(this.employeeContributions.planStartDate).year();
            const currentYear = moment().year();
            const yearsList: number [] = [];
            while (planYear <= currentYear) {
                yearsList.unshift(planYear);
                planYear = planYear + 1;
            }
            return yearsList;
        },
        employeeContributionsSelectedYear: state => state.employeeContributions.year
    },
    actions: {
        setUpcomingPayrollFetchState (fetchState: FetchState) {
            this.upcomingPayrollFetchState = fetchState;
        },
        setUpcomingPayrollData (upcomingPayrollData: PayrollDetail []) {
            this.upcomingPayrolls = upcomingPayrollData;
        },
        setPayrollContributionsGraph (payrollContributionsGraph: PayrollContributionsGraph) {
            this.payrollContributionsGraph.canNavigateBackward = payrollContributionsGraph.canNavigateBackward;
            this.payrollContributionsGraph.canNavigateForward = payrollContributionsGraph.canNavigateForward;
            this.payrollContributionsGraph.entries = _.sortBy(payrollContributionsGraph.entries, "date");
        },
        setPayrollContributionsGraphFetchState (fetchState: FetchState) {
            this.payrollContributionsGraph.fetchState = fetchState;
        },
        setPayrollContributionsGraphPage (page: number) {
            if (page < 1) {
                page = 1;
            }
            this.payrollContributionsGraph.page = page;
        },
        setEmployeeContributionFetchState (fetchState: FetchState) {
            this.employeeContributions.fetchState = fetchState;
        },
        setEmployeeContributionTotalData (result: EmployeeContributionDetail) {
            this.employeeContributions.employeeContributionRecords[result.year] = result;
            this.employeeContributions.planStartDate = result.planStartDate;
            this.employeeContributions.year = result.year;
        },
        setEmployeeContributionsYear (year: number) {
            this.employeeContributions.year = year;
        },
        initUpcomingPayrolls (noOfPayrollsToDisplay: number) {
            if (this.upcomingPayrollFetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            this.setUpcomingPayrollFetchState(FetchState.Loading);
            return PayrollService.getEmployerUpcomingPayrolls(this.userStore.employerId, noOfPayrollsToDisplay)
                .then(response => {
                    this.setUpcomingPayrollData(response.data);
                    this.setUpcomingPayrollFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setUpcomingPayrollFetchState(FetchState.Loaded);
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        initPayrollContributionsGraph () {
            if (this.payrollContributionsGraph.fetchState !== FetchState.Uninitialized) {
                return Promise.resolve();
            }
            if (!this.userStore.employerId) {
                return;
            }

            this.setPayrollContributionsGraphFetchState(FetchState.Loading);
            return PayrollService.getPayrollContributionsDashboardGraph(this.userStore.employerId,
                    this.payrollContributionsGraph.page)
                .then(response => {
                    this.setPayrollContributionsGraph(response.data);
                    this.setPayrollContributionsGraphFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setPayrollContributionsGraphFetchState(FetchState.Loaded);
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        payrollContributionsGraphBack () {
            if (this.payrollContributionsGraph.fetchState !== FetchState.Loaded) {
                return Promise.resolve();
            }

            if (this.canPayrollContributionsGraphNavigateBackward === false) {
                return Promise.resolve();
            }

            const page = this.payrollContributionsGraph.page + 1;
            return PayrollService.getPayrollContributionsDashboardGraph(this.userStore.employerId, page)
                .then(response => {
                    this.setPayrollContributionsGraphPage(page);
                    this.setPayrollContributionsGraph(response.data);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        payrollContributionsGraphForward () {
            if (this.payrollContributionsGraph.fetchState !== FetchState.Loaded) {
                return Promise.resolve();
            }

            if (this.canPayrollContributionsGraphNavigateForward === false) {
                return Promise.resolve();
            }

            const page = this.payrollContributionsGraph.page - 1;
            return PayrollService.getPayrollContributionsDashboardGraph(this.userStore.employerId, page)
                .then(response => {
                    this.setPayrollContributionsGraphPage(page);
                    this.setPayrollContributionsGraph(response.data);
                })
                .catch(err => {
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        },
        initEmployeeContributionsGraph (year: number) {
            if (this.employeeContributions.fetchState !== FetchState.Uninitialized &&
                this.employeeContributions.employeeContributionRecords[year]) {
                if (this.employeeContributions.employeeContributionRecords[year]) {
                    this.setEmployeeContributionsYear(year);
                }
                return Promise.resolve();
            }
            if (!this.userStore.employerId) {
                return;
            }
            this.setEmployeeContributionFetchState(FetchState.Loading);
            return PayrollService.getEmployeeYearToDateContribution(this.userStore.employerId, year)
                .then(response => {
                    this.setEmployeeContributionTotalData(response.data);
                    this.setEmployeeContributionFetchState(FetchState.Loaded);
                })
                .catch(err => {
                    this.setEmployeeContributionFetchState(FetchState.Loaded);
                    storeHelper.handleError({ error: err, rethrow: false });
                });
        }
    }
});